/** @format */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery as useTanstackQuery } from '@tanstack/react-query'
import { FRESHNESS } from '@/shared/api'
import { sessionStore } from '@/shared/storage'

const wmoIcons: Record<number, string> = {
  0: '☀️',
  1: '🌤️',
  2: '⛅',
  3: '☁️',
  45: '🌫️',
  48: '🌫️',
  51: '🌦️',
  53: '🌦️',
  55: '🌧️',
  61: '🌧️',
  63: '🌧️',
  65: '🌧️',
  71: '🌨️',
  73: '🌨️',
  75: '🌨️',
  80: '🌧️',
  81: '🌧️',
  82: '🌧️',
  95: '⛈️',
  96: '⛈️',
  99: '⛈️',
}

const FALLBACK_COORDS = { lat: 41.3, lon: 69.24 } // Tashkent
const round2 = (n: number) => Math.round(n * 100) / 100

function readCachedCoords(): { lat: number; lon: number } | null {
  return sessionStore.flow.get<{ lat: number; lon: number }>('weather-coords')
}

function writeCachedCoords(coords: { lat: number; lon: number }) {
  sessionStore.flow.set('weather-coords', coords)
}

function useGeolocation() {
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(() =>
    readCachedCoords(),
  )

  useEffect(() => {
    if (coords) return // session cache hit — skip permission prompt
    if (!navigator.geolocation) {
      setCoords(FALLBACK_COORDS)
      writeCachedCoords(FALLBACK_COORDS)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = {
          lat: round2(pos.coords.latitude),
          lon: round2(pos.coords.longitude),
        }
        setCoords(next)
        writeCachedCoords(next)
      },
      () => {
        setCoords(FALLBACK_COORDS)
        writeCachedCoords(FALLBACK_COORDS)
      },
      { timeout: 5000 },
    )
  }, [coords])

  return coords
}

async function fetchWeather({ queryKey }: { queryKey: readonly unknown[] }) {
  const [, coords] = queryKey as [string, { lat: number; lon: number }]
  const { lat, lon } = coords

  // Fetch weather and city name in parallel.
  // Both endpoints can be blocked by network/CORS — swallow failures so the
  // header doesn't flood the console with retried errors.
  const [weatherRes, geoRes] = await Promise.allSettled([
    fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code`,
    ),
    fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=10&accept-language=ru`,
    ),
  ])

  if (weatherRes.status !== 'fulfilled' || !weatherRes.value.ok) return null

  const weather = await weatherRes.value.json().catch(() => null)
  if (!weather) return null

  const geo =
    geoRes.status === 'fulfilled' && geoRes.value.ok
      ? await geoRes.value.json().catch(() => null)
      : null
  const city = geo?.address?.city || geo?.address?.town || geo?.address?.state || ''

  return { weather, city }
}

const Weather = () => {
  const { t } = useTranslation()
  const coords = useGeolocation()

  const { data } = useTanstackQuery({
    queryKey: ['weather', coords],
    queryFn: fetchWeather,
    staleTime: FRESHNESS.EXTERNAL,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: !!coords,
  })

  if (!data) return null

  const current = data.weather?.current
  if (!current) return null

  const temp = Math.round(current.temperature_2m)
  const icon = wmoIcons[current.weather_code] || '🌤️'
  const cityName = data.city

  return (
    <div className='flex items-center gap-2 select-none'>
      <span className='text-lg font-medium text-[#212121]'>
        {temp}
        <sup className='text-xs font-normal'>°C</sup>
      </span>
      <span className='text-2xl leading-none'>{icon}</span>
      <div className='flex flex-col leading-tight'>
        <span className='text-xs font-medium text-[#212121]'>{t('today')}</span>
        <span className='text-xs text-[#6e6e6e]'>{cityName}</span>
      </div>
    </div>
  )
}

export default Weather
